import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, useStyles } from '@rexlabs/styling';
import { HeaderLogo } from 'resources/js/personas/byron/header/HeaderLogo';
import { V2Menu } from 'resources/js/personas/byron/header/Menu';
import { DesktopContacts } from 'resources/js/personas/byron/header/DesktopContacts';
import { MobileContacts } from 'resources/js/personas/byron/header/MobileContacts';
import { HamburgerMenu } from 'resources/js/personas/byron/header/HamburgerMenu';

export const defaultStyles = StyleSheet({
  menu: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    boxSizing: 'border-box',
    maxHeight: '70px',
    height: '20px'
  },
  contactsWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginLeft: 'auto',
    boxSizing: 'border-box',
    height: '20px',
    marginTop: '5px',
    marginBottom: '5px',
    '@media(max-width: 1440px)': {
      marginTop: '10px',
      marginBottom: '10px'
    }
  },
  desktopNav: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap-reverse',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
    boxSizing: 'border-box',
    '@media(max-width: 1440px)': {
      display: 'flex',
      alignItems: 'flex-end'
    },
    '@media(max-width: 1024px)': {
      display: 'none'
    }
  },
  mobileNav: {
    display: 'none',
    '@media(max-width: 1024px)': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flex: '1'
    }
  },
  mobileContacts: {
    '@media(max-width: 440px)': {
      display: 'none'
    }
  },
  'bar-container': {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    overflow: 'visible',
    padding: '0px',
    minHeight: '80px'
  },
  menuWrapper: {
    margin: '10px 0px'
  }
});

export type HeaderSocials = {
  facebook: string;
  instagram: string;
  twitter: string;
  pinterest: string;
  youtube: string;
};

export type MenuItem = {
  id: number;
  title: string;
  parent: number;
  url: string;
  external?: boolean;
  children: MenuItem[];
};

export type HeaderMenu = {
  id: number;
  items: MenuItem[];
};

export interface HeaderProps {
  styles: any;
  headerBehaviour: string;
  menuFontColor: string;
  menuFontHoverColor: string;
  menuBackgroundColor: string;
  menuDropdownBackgroundColor: string;
  menuDropdownFontColor: string;
  menuDropdownFontHoverColor: string;
  menuItemHoverColor: string;
  headerAccentColor: string;
  menu: HeaderMenu;
  desktopLogo: string;
  desktopLogoSize: number;
  mobileLogo: string;
  mobileLogoSize: number;
  logoAspectRatio: number;
  socials: HeaderSocials;
  boxed: boolean;
  email: string;
  phone: string;
  enableContactDetails: boolean;
  contactDetailsFontColor: string;
  headerTransparent: boolean;
  transparentFontColor: string;
  transparentHideLogo: boolean;
  forceBurgerMenu: boolean;
  showSocialsInMinimalMenu: boolean;
  onInnerPage: boolean;
}

function HeaderBlock(props: HeaderProps) {
  const s = useStyles(defaultStyles);
  const [navMenuOverflowing, setNavMenuOverflowing] = useState(false);
  const [prevScrollpos, setPrevScrollpos] = useState(window.pageYOffset);
  const [top, setTop] = useState(window.pageYOffset === 0);
  const [isMobileWidth, setIsMobileWidth] = useState<boolean>();
  const headerRef = useRef<HTMLElement>(null);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const headerLogoRef = useRef<HTMLDivElement>(null);
  const headerMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonsRef = useRef<HTMLDivElement>(null);
  const isTransparent = props.headerTransparent && top;
  const headerContainer = document.getElementsByClassName(
    'v2-header-container'
  )[0] as HTMLElement;

  const handleScroll = () => {
    setTop(window.pageYOffset === 0);

    if (props.headerBehaviour === 'hide_and_seek') {
      const currentScrollPos = window.pageYOffset;
      const isVisible =
        prevScrollpos >= currentScrollPos || currentScrollPos <= 0;

      setPrevScrollpos(currentScrollPos);

      if (headerContainer) {
        headerContainer.style.top = isVisible ? '0' : '-250px';
      }
    }
  };

  // We need to create an offset for the inner pages to prevent the
  // absolute positioned header from covering the content
  const setOffset = useCallback(() => {
    if (props.onInnerPage) {
      const topOffset = headerContainer?.offsetHeight
        ? (props.headerTransparent ? 0 : headerContainer.offsetHeight) + 'px'
        : '10px';

      document.body.style.marginTop = topOffset;
      document.documentElement.style.setProperty('--header-height', topOffset);
    }
  }, [props.headerTransparent, props.onInnerPage, headerContainer]);

  // Header Behaviour
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [prevScrollpos, props.headerBehaviour]);

  // Set the offset for the header
  useEffect(() => {
    // We only want to offset the header if we don't allow transparency
    // If we do the offset with transparency enabled then there'll just be a white box
    if (!props.headerTransparent) {
      // Set the initial offset
      setTimeout(setOffset, 200);

      // Listen for resize so we can adjust the margin if required
      window.addEventListener('resize', setOffset);
      return () => {
        window.removeEventListener('resize', setOffset);
      };
    }
  }, [headerRef, props.headerTransparent, setOffset]);

  useEffect(() => {
    const isTransparent = props.headerTransparent && top;

    if (headerContainer) {
      headerContainer.style.backgroundColor = isTransparent
        ? 'rgba(0,0,0,0)'
        : props.menuBackgroundColor;
      headerContainer.style.boxShadow = isTransparent
        ? 'none'
        : '0px 2px 8px rgba(34, 36, 42, 0.12), 0px 4px 8px rgba(34, 36, 42, 0.08)';
    }
  }, [
    top,
    props.headerTransparent,
    props.menuBackgroundColor,
    headerContainer
  ]);

  return (
    <header id='header' ref={headerRef} style={{ height: '100%' }}>
      <div ref={headerContainerRef} {...s('bar-container')}>
        <HeaderLogo
          headerLogoRef={headerLogoRef}
          desktopLogo={props.desktopLogo}
          desktopLogoSize={props.desktopLogoSize}
          mobileLogo={props.mobileLogo}
          mobileLogoSize={props.mobileLogoSize}
          isTransparent={isTransparent}
          transparentHideLogo={props.transparentHideLogo}
          boxed={props.boxed}
          setOffset={setOffset}
          logoAspectRatio={props.logoAspectRatio}
        />

        <div
          ref={headerMenuRef}
          {...s.with('menu')(
            navMenuOverflowing
              ? {
                  paddingRight: '20px'
                }
              : {}
          )}
        >
          <div
            {...s('desktopNav')}
            style={{ display: props.forceBurgerMenu ? 'none' : 'flex' }}
          >
            <div ref={menuButtonsRef} {...s('menuWrapper')}>
              <V2Menu
                menuItemHoverColor={props.menuItemHoverColor}
                menuFontHoverColor={
                  isTransparent
                    ? props.transparentFontColor
                    : props.menuFontHoverColor
                }
                menu={props.menu}
                menuFontColor={
                  isTransparent
                    ? props.transparentFontColor
                    : props.menuFontColor
                }
                menuBackgroundColor={props.menuBackgroundColor}
                menuDropdownBackgroundColor={props.menuDropdownBackgroundColor}
                menuDropdownFontColor={props.menuDropdownFontColor}
                menuDropdownFontHoverColor={props.menuDropdownFontHoverColor}
                mode='horizontal'
                openAnimation='slide-up'
                setNavMenuOverflowing={(x: boolean) => setNavMenuOverflowing(x)}
                isMobileWidth={isMobileWidth}
                setIsMobileWidth={(x: boolean) => setIsMobileWidth(x)}
                headerContainerRef={headerContainerRef}
                headerLogoRef={headerLogoRef}
                headerMenuRef={headerMenuRef}
                headerRef={headerRef}
              />
            </div>

            <DesktopContacts
              enableContactDetails={props.enableContactDetails}
              transparentFontColor={props.transparentFontColor}
              contactDetailsFontColor={props.contactDetailsFontColor}
              menuFontColor={props.menuFontColor}
              menuFontHoverColor={props.menuFontHoverColor}
              email={props.email}
              phone={props.phone}
              socials={props.socials}
              isTransparent={isTransparent}
            />
          </div>

          {props.enableContactDetails && (
            <MobileContacts
              itemColor={
                isTransparent ? props.transparentFontColor : props.menuFontColor
              }
              email={props.email}
              phone={props.phone}
            />
          )}
        </div>

        <HamburgerMenu
          menu={props.menu}
          menuFontColor={props.menuFontColor}
          menuFontHoverColor={props.menuFontHoverColor}
          menuBackgroundColor={props.menuBackgroundColor}
          transparentFontColor={props.transparentFontColor}
          isTransparent={isTransparent}
          navMenuOverflowing={navMenuOverflowing}
          forceBurgerMenu={props.forceBurgerMenu}
          showSocialsInMinimalMenu={props.showSocialsInMinimalMenu}
          socials={props.socials}
        />
      </div>
    </header>
  );
}

export default HeaderBlock;
